import * as React from "react";
import { DataGrid } from "@mui/x-data-grid";
import Paper from "@mui/material/Paper";
import {
  Edit as EditIcon,
  Add as AddIcon,
  Delete as DeleteIcon,
  ChevronLeft,
  ChevronRight,
} from "@mui/icons-material";
import {
  Button,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  CircularProgress,
  Typography,
  Select,
  MenuItem,
  IconButton,
} from "@mui/material";
import { message } from "antd";
import { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";

const PostGetRouter = () => {
  const location = useLocation();
  const [searchText, setSearchText] = useState("");
  const [dataSource, setDataSource] = useState([]);
  const [deleteItemId, setDeleteItemId] = useState(null);
  const [isDeleteModalVisible, setIsDeleteModalVisible] = useState(false);
  const [loading, setLoading] = useState(true);
  const [updatingStatus, setUpdatingStatus] = useState(null);
  const [paginationModel, setPaginationModel] = useState({
    page: 0,
    pageSize: 20,
  });

  useEffect(() => {
    getApiProducts();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [paginationModel]);

  // Refresh data when component becomes visible (e.g., returning from update page)
  useEffect(() => {
    const handleFocus = () => {
      getApiProducts();
    };

    window.addEventListener("focus", handleFocus);
    return () => window.removeEventListener("focus", handleFocus);
  }, []);

  // Refresh data when location changes (returning from update page)
  useEffect(() => {
    if (location.pathname === "/PostGetRouter") {
      getApiProducts();
    }
  }, [location.pathname]);

  const getApiProducts = async () => {
    setLoading(true);
    try {
      const productsRes = await fetch(`https://uktomars.co.uk/api/posts`);
      if (!productsRes.ok) {
        throw new Error(`HTTP error! Status: ${productsRes.status}`);
      }
      const productsData = await productsRes.json();
      setDataSource(
        productsData.map((item, index) => {
          return {
            index: index + 1,
            id: item.id,
            code: item.code,
            gps_id: item.gps_id,
            status: item.status,
          };
        })
      );
    } catch (error) {
      console.error("Error fetching data:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = (id) => {
    setDeleteItemId(id);
    setIsDeleteModalVisible(true);
  };

  const handleConfirmedDelete = async () => {
    setLoading(true);
    try {
      await fetch(`https://uktomars.co.uk/api/posts/${deleteItemId}`, {
        method: "DELETE",
      });
      message.success("Product deleted successfully");
      await getApiProducts();
    } catch (error) {
      console.error("Error deleting product:", error);
      message.error("Error deleting product");
    } finally {
      setLoading(false);
      setIsDeleteModalVisible(false);
    }
  };

  const handleStatusUpdate = async (id, newStatus) => {
    setUpdatingStatus(id);
    try {
      const response = await fetch(`https://uktomars.co.uk/api/posts/${id}`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          status: newStatus,
        }),
      });

      if (response.ok) {
        message.success(
          `Status updated to ${newStatus === 0 ? "Active" : "Completed"}`
        );
        await getApiProducts();
      } else {
        throw new Error("Failed to update status");
      }
    } catch (error) {
      console.error("Error updating status:", error);
      message.error("Failed to update status");
    } finally {
      setUpdatingStatus(null);
    }
  };

  const columns = [
    { field: "index", headerName: "#", width: 70 },
    { field: "id", headerName: "ID", width: 100 },
    { field: "code", headerName: "Code", width: 200 },
    { field: "gps_id", headerName: "GPS ID", width: 150 },
    {
      field: "status",
      headerName: "Status",
      width: 250,
      renderCell: (params) => (
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <Box
            sx={{
              color: Number(params.value) === 0 ? "#2e7d32" : "#d32f2f",
              fontWeight: "bold",
              minWidth: "80px",
            }}
          >
            {Number(params.value) === 0 ? "Active" : "Completed"}
          </Box>
          <Button
            size="small"
            variant="outlined"
            onClick={() =>
              handleStatusUpdate(
                params.row.id,
                Number(params.value) === 0 ? 1 : 0
              )
            }
            disabled={updatingStatus === params.row.id}
            sx={{
              minWidth: "80px",
              height: "28px",
              fontSize: "12px",
              textTransform: "none",
              borderColor: Number(params.value) === 0 ? "#d32f2f" : "#2e7d32",
              color: Number(params.value) === 0 ? "#d32f2f" : "#2e7d32",
              "&:hover": {
                borderColor: Number(params.value) === 0 ? "#d32f2f" : "#2e7d32",
                backgroundColor:
                  Number(params.value) === 0 ? "#ffebee" : "#e8f5e8",
              },
            }}
          >
            {updatingStatus === params.row.id ? (
              <CircularProgress size={16} />
            ) : (
              `Set to ${Number(params.value) === 0 ? "Completed" : "Active"}`
            )}
          </Button>
        </Box>
      ),
    },
    {
      field: "action",
      headerName: "Actions",
      width: 220,
      renderCell: (params) => (
        <Box sx={{ display: "flex", gap: 1 }}>
          <Link to={`/PostGetRouter/UpdatePostRouter/${params.row.id}`}>
            <Button
              variant="contained"
              startIcon={<EditIcon />}
              sx={{
                backgroundColor: "#3A6F43",
                color: "#fff",
                minWidth: 40,
                px: 1.5,
                "&:hover": { backgroundColor: "#3A6F43" },
              }}
            />
          </Link>
          <Button
            variant="contained"
            color="error"
            startIcon={<DeleteIcon />}
            onClick={() => handleDelete(params.row.id)}
            sx={{ minWidth: 40, px: 1.5 }}
          />
        </Box>
      ),
    },
  ];

  const filteredRows = dataSource.filter((row) =>
    Object.values(row).some((val) =>
      String(val).toLowerCase().includes(searchText.toLowerCase())
    )
  );

  const start = paginationModel.page * paginationModel.pageSize;
  const end = start + paginationModel.pageSize;
  const pagedRows = filteredRows.slice(start, end);

  return (
    <div style={{ margin: "25px", overflow: "hidden" }}>
      <Box
        sx={{
          padding: "16px 24px",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          flexWrap: "wrap",
          gap: 2,
          backgroundColor: "#fff",
          borderRadius: "8px",
          boxShadow: "0px 2px 8px rgba(0, 0, 0, 0.1)",
          mb: 2,
        }}
      >
        <TextField
          placeholder="Search Posts"
          variant="outlined"
          sx={{
            flex: 1,
            minWidth: 250,
            maxWidth: 400,
            backgroundColor: "#f5f5f5",
            "& .MuiOutlinedInput-root": {
              "& fieldset": { borderColor: "#ddd" },
              "&:hover fieldset": { borderColor: "#999" },
              "&.Mui-focused fieldset": { borderColor: "#3f51b5" },
            },
          }}
          value={searchText}
          onChange={(e) => setSearchText(e.target.value)}
        />

        <Link to="/PostRouter" style={{ textDecoration: "none" }}>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            sx={{
              backgroundColor: "#3f51b5",
              color: "#fff",
              "&:hover": { backgroundColor: "#303f9f" },
              borderRadius: "6px",
              padding: "10px 20px",
              whiteSpace: "nowrap",
            }}
          >
            Add Post
          </Button>
        </Link>
      </Box>

      {loading ? (
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            height: 300,
          }}
        >
          <CircularProgress size={60} sx={{ color: "#3f51b5" }} />
          <Typography variant="h6" sx={{ mt: 2 }}>
            Loading Posts...
          </Typography>
        </Box>
      ) : (
        <Paper
          sx={{
            width: "100%",
            mb: 10,
            p: 2,
            overflowX: "auto",
            borderRadius: "8px",
            boxShadow: "0px 4px 12px rgba(0, 0, 0, 0.1)",
          }}
        >
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              mb: 2,
              p: 1,
              backgroundColor: "#f5f5f5",
              borderRadius: "4px",
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center" }}>
              <Typography variant="body2" sx={{ mr: 2 }}>
                Rows per page:
              </Typography>
              <Select
                value={paginationModel.pageSize}
                onChange={(e) =>
                  setPaginationModel((prev) => ({
                    ...prev,
                    pageSize: e.target.value,
                    page: 0,
                  }))
                }
                size="small"
                sx={{ height: "36px", minWidth: "80px" }}
              >
                {[10, 20, 50].map((size) => (
                  <MenuItem key={size} value={size}>
                    {size}
                  </MenuItem>
                ))}
              </Select>
            </Box>

            <Typography variant="body2">
              {`${start + 1}-${Math.min(end, filteredRows.length)} of ${
                filteredRows.length
              }`}
            </Typography>

            <Box>
              <IconButton
                onClick={() =>
                  setPaginationModel((prev) => ({
                    ...prev,
                    page: Math.max(prev.page - 1, 0),
                  }))
                }
                disabled={paginationModel.page === 0}
                size="small"
              >
                <ChevronLeft />
              </IconButton>
              <IconButton
                onClick={() =>
                  setPaginationModel((prev) => ({
                    ...prev,
                    page: prev.page + 1,
                  }))
                }
                disabled={
                  (paginationModel.page + 1) * paginationModel.pageSize >=
                  filteredRows.length
                }
                size="small"
              >
                <ChevronRight />
              </IconButton>
            </Box>
          </Box>

          <DataGrid
            rows={pagedRows}
            columns={columns}
            autoHeight
            disableSelectionOnClick
            hideFooter
            sx={{
              minWidth: 800,
              border: 0,
              "& .MuiDataGrid-columnHeaders": {
                backgroundColor: "#f5f5f5",
                color: "black",
                "& .MuiDataGrid-columnHeaderTitle": {
                  fontSize: 16,
                  fontWeight: "bold",
                },
              },
              "& .MuiDataGrid-cell": {
                borderBottom: "1px solid #e0e0e0",
              },
              "& .MuiDataGrid-row:hover": {
                backgroundColor: "#f5f5f5",
              },
            }}
          />
        </Paper>
      )}

      <Dialog
        open={isDeleteModalVisible}
        onClose={() => setIsDeleteModalVisible(false)}
      >
        <DialogTitle>Confirm Delete</DialogTitle>
        <DialogContent>
          <p>Are you sure you want to delete this post?</p>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setIsDeleteModalVisible(false)}>Cancel</Button>
          <Button onClick={handleConfirmedDelete} color="error">
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
};

export default PostGetRouter;
